import { FailureType } from '@app/core/store/_prototypes/action-reducers/actions/helper';
import { LoadState } from '@app/core/store/_prototypes/action-reducers/state/load.state';
import { CoreState } from '@app/core/store/core.slice';
import { Store } from '@ngrx/store';
import { environment } from 'environments/environment';
import { Observable, of, Subject, Subscriber, throwError } from 'rxjs';
import { catchError, delay, filter, first, map, mergeMap, retryWhen, share, switchMap, tap } from 'rxjs/operators';
import { CheckedError } from '@app/core/errors/errors/checked-error.interface';

/**
 * Catch errors that happen on the pipe, and report them to the store.
 *
 * @param store The store to report the error to.
 * @param failureType The action to report the error with.
 */
export const catchErrorsToStore =
  (store: Store<CoreState>, failureType: FailureType<string>) =>
  <T>(source: Observable<T>) =>
    source.pipe(
      catchError((error: any) => {
        const checkedError: CheckedError = {
          errorCode: error?.error?.code ?? 'unknown',
        };

        // Dispatch the error.
        store.dispatch(failureType({ error: checkedError }));

        // Rethrow the error as known error.
        return throwError(() => error);
      })
    );

export const delayedRetry = (delayMs: number, maxRetry) => {
  let retries = maxRetry;

  return (src: Observable<any>) =>
    src.pipe(
      retryWhen((errors: Observable<any>) =>
        errors.pipe(
          delay(delayMs),
          mergeMap((error) => (retries-- > 0 ? of(error) : throwError(() => error)))
        )
      )
    );
};

export const nonNull = () => (src: Observable<any>) => src.pipe(filter((value: any) => !!value));

/**
 * This pipe will make sure the object only gets loaded exactly once.
 *
 * @param observableLoadState The loadstate to check.
 * @param parameters The parameters that we're matching.
 */
export const loadOnce = <T extends { loading: boolean; loaded: false | Parameters }, Parameters extends object>(
  observableLoadState: Observable<T>,
  parameters: Parameters
) =>
  observableLoadState.pipe(
    // Only take one result.
    first(),
    // Filter out, so we only continue if both are false.
    // Do another filter to get rid of the value if the loaded is the same as the parameters.
    filter(
      (loadState: T) =>
        !loadState?.loading &&
        (loadState?.loaded === false || JSON.stringify(loadState?.loaded) !== JSON.stringify(parameters))
    ),
    // Map to void, to make sure the used doesn't get confused.
    map(() => undefined)
  );

/**
 * The map containing all active subscribers.
 */
export const subscriberMap: { [key: string]: number } = {};

/**
 * This is a helper function that keeps the amount of subscribers for any observable.
 * This allows easier debugging of subscriptions and when they are active.
 */
export const subscriberCount = <T>(sourceObservable: Observable<T>, description: string) => {
  // Don't keep this data on production, for performance reasons.
  if (environment.production) {
    return sourceObservable;
  }

  // On non-production, wrap the subscriber function, so we keep a count.
  return new Observable((subscriber: Subscriber<T>) => {
    const subscription = sourceObservable.subscribe(subscriber);

    // Add a subscriber to the map.
    subscriberMap[description] = (subscriberMap[description] || 0) + 1;

    return () => {
      subscription.unsubscribe();

      // Remove a subscriber from the map.
      subscriberMap[description] = subscriberMap[description] - 1;
    };
  });
};

export const awaitLoading = <T>(loadFunction?: () => void) => {
  // Capture the function
  const load = loadFunction ?? (() => {});

  return (src: Observable<LoadState<object, T>>) =>
    src.pipe(
      tap(() => load()),
      filter((state: LoadState<object, T>) => !state.loading && !!state.loaded),
      map((state: LoadState<object, T>) => state.data)
    );
};

export const loadingFinished = (successPipe: () => Observable<boolean>) => {
  return (src: Observable<boolean>) =>
    src.pipe(
      filter((loading) => !!loading),
      switchMap(successPipe),
      filter((loaded) => !!loaded)
    );
};

export const awaitSuccessOrCancel = (successPipe: () => Observable<boolean>) => {
  return (src: Observable<boolean>) =>
    src.pipe(
      filter((loading: boolean) => !loading),
      first(),
      switchMap(successPipe),
      first(),
      filter((success: boolean) => !!success)
    );
};

export const forDropdown = <T>(valueProperty: keyof T, labelProperty: keyof T) => {
  return (src: Observable<T[]>) =>
    src.pipe(
      map((entries) =>
        entries?.map((entry) => ({
          value: entry?.[valueProperty],
          label: entry?.[labelProperty],
        }))
      )
    );
};

export const createProgressReporter = (
  flow: (progressReporter: Subject<{ progress?: string }>) => Promise<boolean>
) => {
  const progressReporter: Subject<{ progress?: string; finished?: boolean; success?: boolean }> = new Subject<{
    progress?: string;
    finished?: boolean;
    success?: boolean;
  }>();

  // Give back the subject, but wrap it, so we only start the flow once the subscription is done.
  return new Observable((subscriber: Subscriber<{ progress?: string; finished?: boolean; success?: boolean }>) => {
    // Start the subscription.
    const subscription = progressReporter.subscribe(subscriber);

    // Start the flow.
    try {
      flow(progressReporter)
        .then((success: boolean) => {
          // At the end of the flow, report the completion.
          progressReporter.next({ finished: true, success });
          progressReporter.complete();
        })
        .catch((error) => progressReporter.error(error));
    } catch (error) {
      // Offload errors to the flow reporter.
      progressReporter.error(error);
    }

    return subscription;
  }).pipe(share());
};

export const formValueLabelPair = (value: string, label: string): { value: string; label: string } => {
  return { value: value, label: label };
}